define((require) => {
    "use strict";

    const Class = require("shared/util/Class");
    const Size  = require("shared/util/Size");

    /**
     * Represents a rectangle.
     *
     * @class Rectangle
     */
    const Rectangle = Class.extend({

        /**
         * Constructor.
         *
         * @param  {Number|Object} top The top distance or a raw object (as the only argument)
         * describing the rectangle, e.g. <tt>{top: 0, left: 0, width: 100, height: 100}</tt>.
         *
         * @param  {Number} left   The left distance.
         * @param  {Number} width  The width of the rectangle.
         * @param  {Number} height The height of the rectangle.
         */
        constructor(top, left, width, height) {
            if (arguments.length === 1) {
                const rectangle = top;

                this.top = rectangle.top || rectangle.y || 0;
                this.left = rectangle.left || rectangle.x || 0;

                this.width = rectangle.width;
                this.height = rectangle.height;
            } else {
                this.top = top;
                this.left = left;
                this.width = width;
                this.height = height;
            }

            // Add bottom and right...
            this.bottom = this.top + this.height;
            this.right = this.left + this.width;

            // Consistency/legacy...
            this.x = this.left;
            this.y = this.top;
        },

        /**
         * Whether the specified rectangle is fully contained within *this* rectangle.
         *
         * @param  {Rectangle} rectangle The rectangle to check.
         * @return {Boolean}   <tt>true</tt> if the specified rectangle is fully contained
         * within *this* rectangle, otherwise <tt>false</tt>.
         */
        contains(rectangle) {
            if ((this.left <= rectangle.left && this.top <= rectangle.top) &&
                ((rectangle.left + rectangle.width) <= (this.left + this.width)) &&
                ((rectangle.top + rectangle.height) <= (this.top + this.height))) {
                return true;
            }

            return false;
        },

        /**
         * Whether the specified rectangle intersects/overlaps with *this* rectangle.
         *
         * @param  {Rectangle} rectangle The rectangle to check.
         * @return {Boolean}              <tt>true</tt> if the specified rectangle intersects/overlaps
         *                                             with *this* rectangle, otherwise <tt>false</tt>.
         */
        intersects(rectangle) {
            // Return false when rectangle is empty (ie has no area)
            if (this.isEmpty()) {
                return false;
            }

            // http://stackoverflow.com/questions/2752349/fast-rectangle-to-rectangle-intersection
            return !(this.left > rectangle.right ||
                this.right < rectangle.left ||
                this.top > rectangle.bottom ||
                this.bottom < rectangle.top);
        },

        area() {
            return this.width * this.height;
        },

        intersectionArea(rectangle) {
            const xOverlap = Math.max(0, Math.min(this.right, rectangle.right) - Math.max(this.left, rectangle.left));
            const yOverlap = Math.max(0, Math.min(this.bottom, rectangle.bottom) - Math.max(this.top, rectangle.top));

            return xOverlap * yOverlap;
        },

        /**
         * Returns a <tt>Size</tt> representation of this Rectangle.
         *
         * @return A new size for this Rectangle as (this.width, this.height); never falsey.
         */
        toSize() {
            return new Size(this.width, this.height);
        },

        /**
         * Determines whether *this* <tt>Rectangle</tt> is empty. When a <tt>Rectangle</tt> is empty, it encloses no area.
         *
         * @return {Boolean} <tt>true</tt> if considered empty, <tt>false</tt> if not.
         */
        isEmpty() {
            return this.width === 0 || this.height === 0;
        },

        toString() {
            return `Rectangle[top: ${this.top}; left: ${this.left}; width: ${this.width}; height: ${this.height}]`;
        },

        /**
         * Returns a new scaled <tt>Rectangle</tt> based on *this*.
         *
         * @param  {Number} factor The scale factor, e.g. <tt>1.50</tt> to scale by 50%.
         * @return {Rectangle}     The new scaled <tt>Rectangle</tt> instance.
         */
        scale(factor) {
            return new Rectangle(this.top * factor, this.left * factor, this.width * factor, this.height * factor);
        }

    });

    return Rectangle;
});



// WEBPACK FOOTER //
// ./src/shared/util/Rectangle.js